import { useNavigate } from '@tanstack/react-router'
import { Select, Switch } from 'antd'
import { usePointCloudContext } from 'features/pointClouds'
import { ClippingSettings } from 'pages/pointClouds/components/hud/controlPanel/ClippingSettings'
import { ColorControls } from 'pages/pointClouds/components/hud/controlPanel/ColorControls'
import { ColorSliders } from 'pages/pointClouds/components/hud/controlPanel/ColorSliders'
import {
  ControlItem,
  ControlLabel,
  ControlPanelWrapper,
  ControlsGrid,
  SectionTitle,
  Title,
} from 'pages/pointClouds/components/hud/controlPanel/ControlPanel.styles'
import { DebugDataDisplay } from 'pages/pointClouds/components/hud/controlPanel/DebugDataDisplay'
import { LegacyRotationSwitch } from 'pages/pointClouds/components/hud/controlPanel/LegacyRotationSwitch'
import { PointSettings } from 'pages/pointClouds/components/hud/controlPanel/PointSettings'
import { stopPropagation } from 'pages/pointClouds/utils'
import {
  getCameraLookAtString,
  getCameraPositionString,
} from 'pages/pointClouds/utils/cameraUtils'
import React, { useEffect, useState } from 'react'
import { createPointCloudViewSearch } from 'routes/point-cloud/-searchParams'

type ControlPanelProps = {
  orgId: string | undefined
  siteId: string
  pointCloudOrSceneId: string | undefined
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  orgId,
  siteId,
  pointCloudOrSceneId,
}) => {
  const navigate = useNavigate()

  const {
    showTiles,
    setShowTiles,
    camera,
    potreePointClouds,
    activePointCloud,
    activePointCloudScene,
    enableTooltips,
    setEnableTooltips,
  } = usePointCloudContext()

  const [cameraPosition, setCameraPosition] = useState<string>('')
  const [lookingAtPosition, setLookingAtPosition] = useState<string>('')

  useEffect(() => {
    const updateCameraPosition = () => {
      if (camera.current) {
        setCameraPosition(getCameraPositionString(camera.current))
        setLookingAtPosition(getCameraLookAtString(camera.current))
      }
    }

    updateCameraPosition()

    const animationFrame = requestAnimationFrame(function animate() {
      updateCameraPosition()
      requestAnimationFrame(animate)
    })

    return () => {
      cancelAnimationFrame(animationFrame)
    }
  }, [camera])

  return (
    <ControlPanelWrapper onWheel={stopPropagation}>
      <Title>Control Panel</Title>

      <ControlsGrid>
        <DebugDataDisplay
          cameraPosition={cameraPosition}
          lookingAtPosition={lookingAtPosition}
        />
      </ControlsGrid>

      {!!activePointCloudScene && (
        <div>
          <SectionTitle>Select Point Cloud</SectionTitle>
          <div style={{ marginBottom: '16px' }}>
            <Select
              style={{ width: '100%' }}
              options={potreePointClouds.map((pc) => ({
                label: pc.id,
                value: pc.id,
              }))}
              value={pointCloudOrSceneId}
              onChange={(value) => {
                navigate({
                  to: '/point-cloud/view',
                  search: createPointCloudViewSearch({
                    orgId: orgId,
                    siteId: siteId,
                    pointCloudOrSceneId: value,
                    cameraPos: undefined,
                    cameraTarget: undefined,
                  }),
                })
              }}
            />
          </div>
        </div>
      )}

      <div>
        <SectionTitle>Rendering Settings</SectionTitle>
        <ControlsGrid>
          {activePointCloud && activePointCloud.id === pointCloudOrSceneId && (
            <ControlItem>
              <LegacyRotationSwitch pointCloud={activePointCloud} />
            </ControlItem>
          )}
          <ControlItem>
            <ControlLabel>Show 3D Tiles</ControlLabel>
            <Switch checked={showTiles} onChange={setShowTiles} />
          </ControlItem>
          <ColorControls />

          {/* Clipping Controls */}
          <ClippingSettings />

          {/* Tooltip Controls */}
          <ControlItem>
            <ControlLabel>Show Thickness Tooltips</ControlLabel>
            <Switch checked={enableTooltips} onChange={setEnableTooltips} />
          </ControlItem>
        </ControlsGrid>

        <div style={{ marginTop: '4px' }}>
          <PointSettings />
          <ColorSliders />
        </div>
      </div>
    </ControlPanelWrapper>
  )
}
